/**
 * EducatorDetail — privacy-tight detail page for a single educator.
 * Recruiter sees ONLY fields the teacher allows. Drives Request CV flow.
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/Skeleton";
import {
  ArrowLeft, Sparkles, Briefcase, MapPin, Globe, GraduationCap, Shield,
  Star, Award, FileText, Loader2, Send, Bookmark, Lock, Eye,
  CheckCircle2, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import ShareButton from "@/components/ShareButton";
import SecureConnectModal from "@/components/SecureConnectModal";

const VERIFICATION_LABELS = {
  email_verified:               "Email verified",
  phone_verified:               "Phone verified",
  profile_verified:             "Profile verified",
  certificate_verified:         "Certificates verified",
  institution_verified:         "Institution verified",
  teebee_verified_educator:     "TeeBee Verified Educator",
  teebee_top_creator:           "TeeBee Top Creator",
  teebee_official_contributor:  "TeeBee Official Contributor",
};

export default function EducatorDetail() {
  const { user_id } = useParams();
  const navigate = useNavigate();
  const [edu, setEdu] = useState(null);
  const [error, setError] = useState(null);
  const [requesting, setRequesting] = useState(false);
  const [shortlisted, setShortlisted] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/marketplace/educators/${user_id}`);
        setEdu(data);
      } catch (err) {
        setError(err?.response?.status === 404 ? "Educator is not discoverable or has private settings." : "Could not load profile.");
      }
    })();
  }, [user_id]);

  const requestCV = async () => {
    setRequesting(true);
    try {
      await api.post("/marketplace/cv-request", { teacher_id: user_id, message: "Interested in your profile via TeeBee Marketplace." });
      setEdu((e) => ({ ...e, cv_request_status: "pending" }));
      toast.success("CV access requested", { description: "The teacher will respond shortly." });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not request");
    } finally {
      setRequesting(false);
    }
  };

  if (error) {
    return (
      <Layout title="Educator">
        <div className="tb-card p-10 text-center max-w-xl mx-auto" data-testid="educator-locked">
          <span className="mx-auto h-14 w-14 grid place-items-center rounded-full bg-amber-50 dark:bg-amber-500/15 mb-3">
            <Lock className="h-6 w-6 text-amber-700 dark:text-amber-300" />
          </span>
          <p className="font-display text-lg font-medium">Profile not accessible</p>
          <p className="mt-1 text-sm text-muted-foreground">{error}</p>
          <Button variant="outline" className="rounded-full mt-4" onClick={() => navigate("/hiring/discover")}>
            <ArrowLeft className="h-3.5 w-3.5 mr-1.5" /> Back to Discover
          </Button>
        </div>
      </Layout>
    );
  }

  if (!edu) {
    return (
      <Layout title="Educator">
        <Skeleton className="h-72 rounded-3xl mb-4" />
        <Skeleton className="h-48" />
      </Layout>
    );
  }

  return (
    <Layout title={edu.name || "Educator"}>
      <Button variant="ghost" size="sm" onClick={() => navigate("/hiring/discover")} className="rounded-full mb-3" data-testid="edu-back">
        <ArrowLeft className="h-3.5 w-3.5 mr-1.5" /> Back to Discover
      </Button>

      {/* Hero */}
      <section className="tb-card-feature relative overflow-hidden" data-testid="edu-hero">
        <div className="absolute inset-0 tb-grid-bg opacity-50 pointer-events-none" />
        <div className="relative flex flex-col md:flex-row gap-5">
          <span className="h-16 w-16 rounded-3xl tb-chip-lavender grid place-items-center font-display text-2xl font-medium shrink-0">
            {(edu.name || "E").slice(0, 1)}
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-display text-3xl tracking-tight font-medium">{edu.name}</h1>
              {edu.verified && <span className="tb-chip-mint rounded-full text-[10px] px-2 py-0.5 font-bold uppercase tracking-wide inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Verified</span>}
            </div>
            <p className="text-sm text-muted-foreground mt-1">{edu.headline || "Educator on TeeBee"}</p>

            {edu.verifications?.length > 0 && (
              <ul className="mt-3 flex flex-wrap gap-1.5" data-testid="edu-badges">
                {edu.verifications.map((v) => (
                  <li key={v} className="tb-chip-sky rounded-full text-[10px] px-2 py-0.5 font-semibold uppercase tracking-wide inline-flex items-center gap-1">
                    <Shield className="h-3 w-3" /> {VERIFICATION_LABELS[v] || v}
                  </li>
                ))}
              </ul>
            )}

            <ul className="mt-4 flex flex-wrap gap-3 text-[12px] text-muted-foreground">
              {edu.experience_years != null && <li className="inline-flex items-center gap-1.5"><Briefcase className="h-3 w-3" /> {edu.experience_years} yrs</li>}
              {edu.preferred_location && <li className="inline-flex items-center gap-1.5"><MapPin className="h-3 w-3" /> {edu.preferred_location}</li>}
              {edu.modes?.length > 0 && <li className="inline-flex items-center gap-1.5"><Globe className="h-3 w-3" /> {edu.modes.join(" · ")}</li>}
              {edu.salary_band && <li className="inline-flex items-center gap-1.5"><Sparkles className="h-3 w-3" /> {edu.salary_band}</li>}
            </ul>

            <div className="mt-5 flex flex-wrap gap-2">
              {edu.cv_request_status === "pending" ? (
                <Button variant="outline" className="rounded-full" disabled data-testid="edu-cv-pending">
                  <FileText className="h-3.5 w-3.5 mr-1.5" /> CV request pending
                </Button>
              ) : edu.cv_request_status === "approved" ? (
                <Button variant="outline" className="rounded-full" disabled data-testid="edu-cv-approved">
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> CV access granted
                </Button>
              ) : (
                <Button onClick={requestCV} disabled={requesting} className="rounded-full" data-testid="edu-cv-request">
                  {requesting ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <FileText className="h-3.5 w-3.5 mr-1.5" />}
                  Request CV
                </Button>
              )}
              <Button variant="outline" className="rounded-full" onClick={() => setShortlisted((s) => !s)} data-testid="edu-shortlist">
                <Star className={`h-3.5 w-3.5 mr-1.5 ${shortlisted ? "fill-current text-amber-500" : ""}`} /> {shortlisted ? "Shortlisted" : "Shortlist"}
              </Button>
              <Button variant="outline" className="rounded-full" data-testid="edu-save">
                <Bookmark className="h-3.5 w-3.5 mr-1.5" /> Save
              </Button>
              <Button
                onClick={() => setConnectOpen(true)}
                className="rounded-full bg-gradient-to-r from-violet-600 via-fuchsia-600 to-amber-500 hover:opacity-90 text-white shadow-md"
                data-testid="edu-secure-connect"
              >
                <Lock className="h-3.5 w-3.5 mr-1.5" /> Secure Connect · ₹199
              </Button>
              <ShareButton kind="profile" target_id={edu.public_profile_path?.split("/").pop() || edu.user_id} />
            </div>
          </div>

          <aside className="md:w-56 shrink-0 space-y-3">
            <div className="rounded-2xl border border-border p-4 text-center">
              <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground">Profile strength</p>
              <p className="font-display text-3xl tracking-tight font-medium mt-1">{edu.profile_strength || 0}<span className="text-base text-muted-foreground">/100</span></p>
              <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
                <div className="h-full bg-primary transition-all" style={{ width: `${edu.profile_strength || 0}%` }} />
              </div>
            </div>
            {edu.public_profile_path && (
              <Button variant="outline" className="rounded-full w-full" onClick={() => window.open(edu.public_profile_path, "_blank")} data-testid="edu-public-link">
                <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Public profile
              </Button>
            )}
          </aside>
        </div>
      </section>

      {/* Detail grid */}
      <section className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          {edu.bio && (
            <div className="tb-card p-5" data-testid="edu-bio">
              <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground mb-2 inline-flex items-center gap-2"><Eye className="h-3 w-3" /> About</p>
              <p className="text-sm leading-relaxed whitespace-pre-line">{edu.bio}</p>
            </div>
          )}
          <div className="tb-card p-5" data-testid="edu-categories">
            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground mb-2 inline-flex items-center gap-2"><GraduationCap className="h-3 w-3" /> Categories & subjects</p>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {(edu.teaching_categories || []).map((c) => (
                <span key={c} className="tb-chip-lavender rounded-full text-[11px] px-2 py-1">{c}</span>
              ))}
            </div>
            {edu.subjects?.length > 0 && (
              <p className="text-sm"><span className="text-muted-foreground">Subjects: </span>{edu.subjects.join(", ")}</p>
            )}
            {edu.languages_taught?.length > 0 && (
              <p className="text-sm mt-1"><span className="text-muted-foreground">Languages: </span>{edu.languages_taught.join(", ")}</p>
            )}
          </div>
          {(edu.achievements?.length > 0 || edu.certifications?.length > 0) && (
            <div className="tb-card p-5" data-testid="edu-achievements">
              <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground mb-2 inline-flex items-center gap-2"><Award className="h-3 w-3" /> Achievements & certifications</p>
              {edu.achievements?.length > 0 && (
                <>
                  <p className="text-xs text-muted-foreground mb-1">Achievements</p>
                  <ul className="list-disc ml-4 text-sm space-y-0.5 mb-3">{edu.achievements.map((a, i) => <li key={`ach-${i}-${(a || '').slice(0, 24)}`}>{a}</li>)}</ul>
                </>
              )}
              {edu.certifications?.length > 0 && (
                <>
                  <p className="text-xs text-muted-foreground mb-1">Certifications</p>
                  <ul className="list-disc ml-4 text-sm space-y-0.5">{edu.certifications.map((c, i) => <li key={`cert-${i}-${(c || '').slice(0, 24)}`}>{c}</li>)}</ul>
                </>
              )}
            </div>
          )}
          {edu.expertise?.length > 0 && (
            <div className="tb-card p-5" data-testid="edu-expertise">
              <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground mb-2">Areas of expertise</p>
              <div className="flex flex-wrap gap-1.5">{edu.expertise.map((e, i) => <span key={`exp-${i}-${(e || '').slice(0, 24)}`} className="tb-chip-sky rounded-full text-[11px] px-2 py-1">{e}</span>)}</div>
            </div>
          )}
        </div>

        <aside className="space-y-5">
          <div className="tb-card p-5" data-testid="edu-modes">
            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground mb-2">Teaching modes</p>
            <div className="flex flex-wrap gap-1.5">
              {edu.modes?.length > 0
                ? edu.modes.map((m) => <span key={m} className="tb-chip-mint rounded-full text-[11px] px-2 py-1">{m}</span>)
                : <span className="text-xs text-muted-foreground italic">Not specified</span>}
            </div>
          </div>
          <div className="tb-card p-5" data-testid="edu-availability">
            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground mb-2">Availability</p>
            <p className="text-sm">{edu.available_for_hiring ? "Available for hiring" : "Not actively looking"}</p>
            {edu.salary_band && <p className="text-xs text-muted-foreground mt-1">{edu.salary_band}</p>}
          </div>
        </aside>
      </section>

      <SecureConnectModal
        open={connectOpen}
        onClose={() => setConnectOpen(false)}
        recipientUserId={edu.user_id}
        recipientName={edu.display_name}
      />
    </Layout>
  );
}
